import { getGridView } from '@simplywallst/services';
import { useQuery } from '@tanstack/react-query';
import { companyMapper } from '@/hooks/useGridCompanies';
import type { Company as GridCompany } from '@/hooks/useGridCompanies';
import { ROUTE_DISCOVER_INVESTING_IDEAS } from '@/constants/routes';
import type { BreadcrumbItem } from '@components/Breadcrumb';
import { normaliseResponse as normaliseGridView } from '@/hooks/useGridView';
import type { GridView } from '@/hooks/useGridView';
type Grid = Awaited<ReturnType<typeof getGridView>>['data']['data'];
export interface Company extends GridCompany {
  narrativeHTML?: string;
}

type Content = {
  title?: string;
  intro?: string;
  author?: string;
  authorUrl?: string;
  authorImageUrl?: string;
  companies?: Company[];
  seoTitle?: string;
  seoDescription?: string;
  disclaimer?: string;
};

export type CommunityIdea = GridView &
  Content & {
    breadCrumbs: BreadcrumbItem[];
  };

export const getQueryKey = <T>(id: T extends string ? T : never) =>
  [`COMMUNITY_IDEA_${id}`] as const;

const normaliseContent = ({ content_blocks, seo }: Grid) => {
  const content: Content = {};
  content_blocks?.data.forEach((n) => {
    switch (n.type) {
      case 'intro':
        content.intro = n.content;
        break;
      case 'author':
        content.author = n.content;
        content.authorUrl = n.author_url;
        content.authorImageUrl = n.image_url;
        break;
      case 'title':
        content.title = n.content;
        break;
      case 'disclaimer':
        content.disclaimer = n.content;
        break;
      case 'company-narrative':
        const company = companyMapper(n.company.data);
        if (typeof company === 'undefined') break;
        if (typeof content.companies === 'undefined') {
          content.companies = [];
        }
        content.companies.push({
          narrativeHTML: n.content,
          ...company,
        });
        break;
    }
  });
  return {
    ...content,
    seoTitle: seo?.title,
    seoDescription: seo?.description,
  };
};

export const makeQueryFn = (id: string) => async (): Promise<CommunityIdea> => {
  const response = await getGridView(id);
  const gridView = normaliseGridView(response.data.data);
  const content = normaliseContent(response.data.data);
  const breadCrumbs = [
    {
      name: 'Investing Ideas',
      dispatch: { type: ROUTE_DISCOVER_INVESTING_IDEAS },
    },
    { name: gridView.name },
  ];

  return {
    ...gridView,
    ...content,
    breadCrumbs,
  };
};

/**
 * ### Description
 *
 * `useCommunityIdea` requests a grid view via the `getGridView` service.
 *
 * This hook could be used for any grid view, but ideally it should be
 * focused on views with `type_slug: narrative-collection`.
 *
 * ### Params
 * 1. `id` which defaults to `'0'`. When `id === '0'` the hook's `useQuery` will be disabled
 *
 * ### Returns
 * - UseQueryResult<CommunityIdea>
 */
export function useCommunityIdea(id = '0') {
  const queryKey = getQueryKey(id);
  /**
   *
   * @TODO make this query comply with exhaustive deps rule
   * eg. `id` should be passed as a queryKey
   *
   */
  return useQuery({
    queryKey,
    queryFn: makeQueryFn(id),
    retry: false,
    enabled: id !== '0',
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}
